/**
 * PDF を新しいタブで開く（docs/72 §10、2026-10-13）。
 * サーバーは同時に作る PDF の数に上限があり、超えた分は順番を待つ。前は `<a target="_blank">` で開いていたので、待つあいだ画面には何も出ず、
 * 押すたびに新しいタブと PDF の作成が増えた。
 *   ・押した瞬間にタブを開いて「PDF 作成中…」と書く（取ってからタブを開くと Safari が止める）
 *   ・できたら差し替える：保存される PDF（控え・送った見積依頼書）は同じ URL へ（2回目は保存した PDF を返すだけ＝保存の名前もそのまま）、
 *     保存しない PDF（写真台帳）は取った中身をそのまま見せる（もう一度作らせない）
 *   ・作成中は同じ口を押しても何もしない（ボタンは busy を見て「PDF 作成中…」・押せない）
 *   ・断られたら（混み合い・ログイン切れ）タブを閉じて、誤りの通知
 */
import { MSG } from "@genbaryoku/contract";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchFile, messageOf } from "../api.js";
import { notify } from "../components/Notice.js";

/** 開いたばかりの空のタブに「PDF 作成中…」と書く（書けなくても続ける）。先にタブを開いてから PDF を作る画面（見積依頼書の印刷）も使う */
export function writeMaking(w: Window): void {
  try {
    const d = w.document;
    d.title = MSG.pdfMaking;
    d.body.style.cssText = "margin:0;padding:32px;font:17px/1.7 sans-serif;color:#55534C;background:#fff";
    d.body.textContent = MSG.pdfMaking;
  } catch {
    /* 書けない窓（ブラウザの決まり）でも、差し替えは続ける */
  }
}

export type PdfTab = {
  /** いま作っている URL（無ければ null）。ボタンの文字・押せるかに使う */
  busy: string | null;
  /** url を新しいタブで開く。stored＝作ったあとサーバーに保存される PDF（同じ URL へ移る）。開けたら true（作成中に押したとき・断られたときは false） */
  open: (url: string, opts?: { stored?: boolean }) => Promise<boolean>;
};

/** PDF を新しいタブで開く口。断られたら誤りの通知（右下・消えない） */
export function usePdfTab(): PdfTab {
  const [busy, setBusy] = useState<string | null>(null);
  const busyRef = useRef<string | null>(null);
  // 画面が生きているか（離れたあとに状態を書かない）。開発の StrictMode は付け外しを2回するので、付けるたびに true に戻す
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const open = useCallback(
    (url: string, opts: { stored?: boolean } = {}): Promise<boolean> => {
      if (busyRef.current) return Promise.resolve(false); // 作成中は押しても何もしない
      busyRef.current = url;
      setBusy(url);
      const w = window.open("", "_blank");
      if (w) writeMaking(w);
      return fetchFile(url)
        .then((blob) => {
          const href = opts.stored ? url : URL.createObjectURL(blob);
          if (w && !w.closed) w.location.href = href;
          else if (!w) window.location.href = href; // 新しいタブを止められた：この画面で開く
          return true;
        })
        .catch((e: unknown) => {
          w?.close();
          notify({ text: messageOf(e), tone: "error", testId: "pdf-error" });
          return false;
        })
        .finally(() => {
          busyRef.current = null;
          if (alive.current) setBusy(null);
        });
    },
    [],
  );
  return { busy, open };
}
